import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Chip, Grid,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Alert, CircularProgress, Avatar, MenuItem, TextField,
  LinearProgress, InputAdornment, Tooltip,
} from '@mui/material';
import { WorkOutline, Search, PersonSearch, Warning } from '@mui/icons-material';

function apiFetch(path: string, opts: RequestInit = {}) {
  return fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4000'}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}`, ...(opts.headers || {}) },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

const STATUS_CFG = {
  filled:  { label: 'Filled',    color: '#2e7d32', bg: '#e8f5e9', emoji: '✅' },
  vacant:  { label: 'Vacant',    color: '#b71c1c', bg: '#ffebee', emoji: '🔴' },
  frozen:  { label: 'Frozen',    color: '#546e7a', bg: '#eceff1', emoji: '🔒' },
  new:     { label: 'New',       color: '#1565c0', bg: '#e3f2fd', emoji: '🆕' },
};

const LEVEL_CFG: Record<string, { label: string; color: string }> = {
  junior:     { label: 'Junior',     color: '#78909c' },
  mid:        { label: 'Mid',        color: '#1565c0' },
  senior:     { label: 'Senior',     color: '#6a1b9a' },
  lead:       { label: 'Lead',       color: '#e65100' },
  manager:    { label: 'Manager',    color: '#c62828' },
  director:   { label: 'Director',   color: '#37474f' },
  vp:         { label: 'VP',         color: '#212121' },
  c_level:    { label: 'C-Level',    color: '#000' },
  intern:     { label: 'Intern',     color: '#26a69a' },
  contractor: { label: 'Contractor', color: '#8d6e63' },
};

interface Position {
  id: string; title: string; level?: string; employmentType?: string;
  deptName: string; deptId: string; entityName: string;
  headcountFilled: number; incumbentNames?: string;
  openReqs: number; status: string;
  gradeCode?: string; gradeTitle?: string;
  salaryMin?: number; salaryMax?: number; salaryMid?: number; salaryCurrency?: string;
}

export default function PositionRegistry() {
  const [tab, setTab]         = useState(0);
  const [positions, setPositions] = useState<Position[]>([]);
  const [recruiting, setRecruiting] = useState<any[]>([]);
  const [vacancySummary, setVacancySummary] = useState<any[]>([]);
  const [stats, setStats]     = useState<any>(null);
  const [depts, setDepts]     = useState<any[]>([]);
  const [deptFilter, setDeptFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch]   = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (deptFilter) params.set('departmentId', deptFilter);

      const [pos, rec, vs, st, deptList] = await Promise.all([
        apiFetch(`/api/position-registry?${params}`),
        apiFetch(`/api/position-registry/actively-recruiting${deptFilter ? `?departmentId=${deptFilter}` : ''}`),
        apiFetch('/api/position-registry/vacancy-summary'),
        apiFetch('/api/position-registry/stats'),
        apiFetch('/api/departments'),
      ]);

      setPositions(pos ?? []);
      setRecruiting(rec ?? []);
      setVacancySummary(vs ?? []);
      setStats(st);
      setDepts(Array.isArray(deptList) ? deptList : (deptList.data ?? []));
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }, [deptFilter]);

  useEffect(() => { load(); }, [load]);

  const filtered = positions.filter(p => {
    if (statusFilter && p.status !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return p.title.toLowerCase().includes(q) || p.deptName?.toLowerCase().includes(q) ||
             p.incumbentNames?.toLowerCase().includes(q) || false;
    }
    return true;
  });

  const totalVacant   = positions.filter(p => p.status === 'vacant').length;
  const totalFilled   = positions.filter(p => p.status === 'filled').length;
  const fillRate      = positions.length > 0 ? Math.round((totalFilled / positions.length) * 100) : 0;

  const fmt = (n?: number, ccy = 'HKD') =>
    n ? `${ccy} ${n.toLocaleString('en-HK', { maximumFractionDigits: 0 })}` : '—';

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Avatar sx={{ bgcolor: '#37474f', width: 48, height: 48 }}><WorkOutline /></Avatar>
        <Box>
          <Typography variant="h5" fontWeight={700}>Position Registry</Typography>
          <Typography variant="body2" color="text.secondary">
            All approved positions · incumbents · vacancy status · salary bands · open recruitment
          </Typography>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* KPI cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Total Positions',  value: stats.totalPositions,  color: '#546e7a' },
            { label: 'Filled',           value: totalFilled,            color: '#2e7d32' },
            { label: 'Vacant',           value: totalVacant,            color: '#b71c1c' },
            { label: 'Fill Rate',        value: `${fillRate}%`,         color: fillRate >= 90 ? '#2e7d32' : fillRate >= 75 ? '#f57f17' : '#b71c1c' },
            { label: 'Open Requisitions', value: stats.openReqs,        color: '#6a1b9a' },
            { label: 'Actively Recruiting', value: recruiting.length,   color: '#1565c0' },
          ].map(s => (
            <Grid size={{ xs: 6, sm: 4, md: 2 }} key={s.label}>
              <Paper sx={{ p: 1.5, textAlign: 'center', borderTop: `3px solid ${s.color}` }}>
                <Typography variant="h4" fontWeight={700} color={s.color}>{s.value}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2, display: 'block' }}>{s.label}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {totalVacant > 0 && (
        <Alert severity="warning" icon={<Warning />} sx={{ mb: 2 }}>
          <strong>{totalVacant} vacant position{totalVacant !== 1 ? 's' : ''}</strong> — {recruiting.length} with active recruitment underway.
        </Alert>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="All Positions" />
        <Tab label="Actively Recruiting" />
        <Tab label="Vacancy by Department" />
      </Tabs>

      {/* Filters */}
      <Paper sx={{ p: 1.5, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField size="small" placeholder="Search title / incumbent / dept…" value={search}
          onChange={e => setSearch(e.target.value)} sx={{ minWidth: 240 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
        <TextField select size="small" label="Department" value={deptFilter}
          onChange={e => setDeptFilter(e.target.value)} sx={{ minWidth: 180 }}>
          <MenuItem value="">All Departments</MenuItem>
          {depts.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
        </TextField>
        {tab === 0 && (
          <TextField select size="small" label="Status" value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)} sx={{ minWidth: 130 }}>
            <MenuItem value="">All Statuses</MenuItem>
            {Object.entries(STATUS_CFG).map(([k, v]) => (
              <MenuItem key={k} value={k}>{v.emoji} {v.label}</MenuItem>
            ))}
          </TextField>
        )}
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>
      ) : (
        <>
          {/* ── All Positions ── */}
          {tab === 0 && (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Position Title</TableCell>
                    <TableCell>Department</TableCell>
                    <TableCell>Level</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Incumbent(s)</TableCell>
                    <TableCell align="right">Open Reqs</TableCell>
                    <TableCell>Grade</TableCell>
                    <TableCell>Salary Band</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        No positions match the selected filters.
                      </TableCell>
                    </TableRow>
                  )}
                  {filtered.map(p => {
                    const sc  = STATUS_CFG[p.status as keyof typeof STATUS_CFG] ?? STATUS_CFG.vacant;
                    const lc  = p.level ? (LEVEL_CFG[p.level.toLowerCase()] ?? { label: p.level, color: '#546e7a' }) : null;
                    return (
                      <TableRow key={p.id} hover sx={{ bgcolor: p.status === 'vacant' ? '#ffebee10' : undefined }}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>{p.title}</Typography>
                          <Typography variant="caption" color="text.secondary">{p.entityName}</Typography>
                        </TableCell>
                        <TableCell>{p.deptName ?? '—'}</TableCell>
                        <TableCell>
                          {lc ? (
                            <Chip size="small" label={lc.label}
                              sx={{ bgcolor: lc.color + '15', color: lc.color, fontSize: '0.65rem' }} />
                          ) : '—'}
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={`${sc.emoji} ${sc.label}`}
                            sx={{ bgcolor: sc.bg, color: sc.color, fontSize: '0.65rem' }} />
                        </TableCell>
                        <TableCell>
                          {p.incumbentNames ? (
                            <Tooltip title={p.incumbentNames}>
                              <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {p.headcountFilled > 1 ? `${p.headcountFilled} people` : p.incumbentNames}
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Typography variant="caption" color="text.disabled">Vacant</Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {p.openReqs > 0 ? (
                            <Chip size="small" label={`${p.openReqs} open`}
                              sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontSize: '0.65rem' }} />
                          ) : '—'}
                        </TableCell>
                        <TableCell>
                          {p.gradeCode ? (
                            <Chip size="small" label={`${p.gradeCode} ${p.gradeTitle ?? ''}`}
                              sx={{ fontSize: '0.65rem', bgcolor: '#f5f5f5' }} />
                          ) : '—'}
                        </TableCell>
                        <TableCell>
                          {p.salaryMin && p.salaryMax ? (
                            <Typography variant="caption">
                              {fmt(p.salaryMin, p.salaryCurrency)} – {fmt(p.salaryMax, p.salaryCurrency)}
                            </Typography>
                          ) : '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* ── Actively Recruiting ── */}
          {tab === 1 && (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Position</TableCell>
                    <TableCell>Department</TableCell>
                    <TableCell align="right">Open Reqs</TableCell>
                    <TableCell align="right">Total Openings</TableCell>
                    <TableCell align="right">Active Candidates</TableCell>
                    <TableCell align="right">Oldest Req Age</TableCell>
                    <TableCell>Urgency</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recruiting.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        No positions with active recruitment.
                      </TableCell>
                    </TableRow>
                  )}
                  {recruiting.map((r: any, i: number) => {
                    const days = parseInt(r.maxDaysOpen || 0);
                    const urgency = days >= 90 ? { label: 'Critical', color: '#b71c1c', bg: '#ffebee' }
                      : days >= 45 ? { label: 'High', color: '#e65100', bg: '#fff3e0' }
                      : { label: 'Normal', color: '#2e7d32', bg: '#e8f5e9' };
                    return (
                      <TableRow key={i} hover sx={{ bgcolor: days >= 90 ? '#ffebee10' : undefined }}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>{r.reqTitle ?? r.title}</Typography>
                          {r.level && <Chip size="small" label={r.level} sx={{ fontSize: '0.6rem', ml: 0.5 }} />}
                        </TableCell>
                        <TableCell>{r.dept}</TableCell>
                        <TableCell align="right">{r.openReqs}</TableCell>
                        <TableCell align="right">{r.openingsCount ?? '—'}</TableCell>
                        <TableCell align="right">
                          <Typography fontWeight={600} color={parseInt(r.activeCandidates) > 0 ? '#1565c0' : 'text.secondary'}>
                            {r.activeCandidates}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight={700} color={urgency.color}>{days}d</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={urgency.label}
                            sx={{ bgcolor: urgency.bg, color: urgency.color, fontSize: '0.65rem' }} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* ── Vacancy by Department ── */}
          {tab === 2 && (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Department</TableCell>
                    <TableCell align="right">Total Positions</TableCell>
                    <TableCell align="right">Filled</TableCell>
                    <TableCell align="right">Vacant</TableCell>
                    <TableCell align="right">Fill Rate</TableCell>
                    <TableCell>Coverage Bar</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {vacancySummary.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>No data</TableCell>
                    </TableRow>
                  )}
                  {vacancySummary.map((d: any, i: number) => {
                    const rate = parseFloat(d.fillRate || 0);
                    const barColor = rate >= 90 ? '#2e7d32' : rate >= 75 ? '#f57f17' : '#b71c1c';
                    return (
                      <TableRow key={i} hover>
                        <TableCell><Typography variant="body2" fontWeight={600}>{d.dept ?? 'Unassigned'}</Typography></TableCell>
                        <TableCell align="right">{d.totalPositions}</TableCell>
                        <TableCell align="right" sx={{ color: '#2e7d32', fontWeight: 700 }}>{d.filled}</TableCell>
                        <TableCell align="right">
                          {parseInt(d.vacant) > 0 ? (
                            <Typography fontWeight={700} color="#b71c1c">{d.vacant}</Typography>
                          ) : <Typography color="text.secondary">0</Typography>}
                        </TableCell>
                        <TableCell align="right">
                          <Chip size="small" label={`${rate.toFixed(1)}%`}
                            sx={{ bgcolor: barColor + '15', color: barColor, fontWeight: 700, fontSize: '0.65rem' }} />
                        </TableCell>
                        <TableCell sx={{ minWidth: 160 }}>
                          <LinearProgress variant="determinate" value={rate}
                            sx={{ height: 10, borderRadius: 4, '& .MuiLinearProgress-bar': { bgcolor: barColor } }} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </Box>
  );
}
